import {
  ID,
  SortingMode,
  Task,
  TaskListTreeItem,
  TaskPriorityOptions,
  Timestamp,
  WeekDays,
} from '@griply/types';
import { buildPriorityTree } from '@griply/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNewTaskRow?: boolean;
};

export const usePriorityTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNewTaskRow,
}: Options) => {
  const { t } = useTranslation();

  const priorityLabels = useMemo<Record<TaskPriorityOptions, string>>(
    () => ({
      [TaskPriorityOptions.High]: t(
        `general.labels.priority.options.${TaskPriorityOptions.High}`,
      ),
      [TaskPriorityOptions.Medium]: t(
        `general.labels.priority.options.${TaskPriorityOptions.Medium}`,
      ),
      [TaskPriorityOptions.Low]: t(
        `general.labels.priority.options.${TaskPriorityOptions.Low}`,
      ),
      [TaskPriorityOptions.None]: t(
        `general.labels.priority.options.${TaskPriorityOptions.None}`,
      ),
    }),
    [t],
  );

  return useMemo<Record<ID, TaskListTreeItem>>(
    () =>
      buildPriorityTree({
        tasks,
        customOrder,
        sortingMode,
        today,
        weekStartsOn,
        showEmptyGroups,
        hideNewTaskRow,
        priorityLabels,
        rootLabel: 'Root',
        newTaskLabel: 'New Task',
      }),
    [
      customOrder,
      sortingMode,
      tasks,
      today,
      weekStartsOn,
      showEmptyGroups,
      hideNewTaskRow,
      priorityLabels,
    ],
  );
};
